/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import { apiClient } from '../../services/api';

// CSS for highlighting mustache templates in the editor
const TEMPLATE_CSS = `
  .mce-mustache-template {
    background-color: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 3px;
    padding: 1px 4px;
    color: #92400e;
    font-weight: 600;
    cursor: pointer;
    display: inline-block;
  }
  .mce-mustache-template:hover {
    background-color: #fde68a;
    border-color: #d97706;
  }
`;

// Convert raw mustache patterns to highlighted spans
function wrapMustachePatterns(content: string): string {
  return content.replace(
    /\{\{([^}]+)\}\}/g,
    '<span class="mce-mustache-template" contenteditable="false" data-mce-pattern="$1">{{$1}}</span>'
  );
}

// Strip highlighted spans back to raw mustache patterns
function unwrapMustachePatterns(content: string): string {
  return content.replace(
    /<span[^>]*class="mce-mustache-template"[^>]*>\{\{([^}]+)\}\}<\/span>/g,
    '{{$1}}'
  );
}

export interface MustachePluginConfig {
  collectionId: number | null;
  mode: 'author' | 'fill'; // 'author' = create/edit templates, 'fill' = fill in values
}

export function registerMustachePlugin(tinymce: any) {
  // Guard against undefined tinymce (e.g., in test environment)
  if (!tinymce || !tinymce.PluginManager) {
    return;
  }
  if (tinymce.PluginManager.get('mustachetemplate')) {
    return; // Already registered
  }

  tinymce.PluginManager.add('mustachetemplate', (editor: any) => {
    // Register the custom option
    editor.options.register('mustacheTemplateConfig', {
      processor: 'object',
      default: { collectionId: null, mode: 'author' }
    });

    const config: MustachePluginConfig = editor.options.get('mustacheTemplateConfig') || { collectionId: null, mode: 'author' };

    // Add content CSS for template highlighting
    editor.on('init', () => {
      const doc = editor.getDoc();
      if (doc) {
        const style = doc.createElement('style');
        style.textContent = TEMPLATE_CSS;
        doc.head.appendChild(style);
      }

      // Wrap existing mustache patterns on load
      const content = editor.getContent();
      if (content.includes('{{')) {
        editor.setContent(wrapMustachePatterns(content));
      }
    });

    // When setting content (e.g., loading from DB), wrap patterns
    editor.on('BeforeSetContent', (e: any) => {
      if (e.content && e.content.includes('{{') && !e.content.includes('mce-mustache-template')) {
        e.content = wrapMustachePatterns(e.content);
      }
    });

    // When getting content (e.g., saving to DB), unwrap patterns
    editor.on('GetContent', (e: any) => {
      if (e.content) {
        e.content = unwrapMustachePatterns(e.content);
      }
    });

    // Click handler for template spans
    editor.on('click', (e: any) => {
      const target = e.target;
      if (target && target.classList && target.classList.contains('mce-mustache-template')) {
        const pattern = target.getAttribute('data-mce-pattern') || target.textContent.replace(/[{}]/g, '');
        if (config.mode === 'author') {
          openAuthorDialog(editor, config.collectionId, pattern, target);
        } else {
          openFillAllDialog(editor, config.collectionId);
        }
      }
    });

    // Toolbar button: Insert Template
    if (config.mode === 'author') {
      editor.ui.registry.addButton('inserttemplate', {
        text: 'Insert Template',
        tooltip: 'Insert a mustache template variable',
        onAction: () => {
          openAuthorDialog(editor, config.collectionId, '', null);
        }
      });
    }

    return {
      getMetadata: () => ({
        name: 'Mustache Template Plugin',
        url: ''
      })
    };
  });
}

// Dialog for authoring/editing template definitions
async function openAuthorDialog(editor: any, collectionId: number | null, existingPattern: string, targetElement: HTMLElement | null) {
  let existingItem: any = null;

  // If editing an existing pattern, fetch its data from the API
  if (existingPattern && collectionId) {
    try {
      const response = await apiClient.getTemplateItems(collectionId);
      if (response.success && response.items) {
        existingItem = response.items.find((item: any) => item.templatePattern === existingPattern);
      }
    } catch (err) {
      console.error('Error fetching template items:', err);
    }
  }

  editor.windowManager.open({
    title: existingPattern ? 'Edit Template Variable' : 'Insert Template Variable',
    body: {
      type: 'panel',
      items: [
        {
          type: 'input',
          name: 'templatePattern',
          label: 'Template Pattern',
          placeholder: 'e.g., company_name'
        },
        {
          type: 'selectbox',
          name: 'dataType',
          label: 'Data Type',
          items: [
            { text: 'Text', value: 'text' },
            { text: 'Number', value: 'number' },
            { text: 'Price', value: 'price' },
            { text: 'Date', value: 'date' }
          ]
        },
        {
          type: 'input',
          name: 'defaultValue',
          label: 'Default Value',
          placeholder: 'Optional default value'
        }
      ]
    },
    initialData: {
      templatePattern: existingItem?.templatePattern || existingPattern || '',
      dataType: existingItem?.dataType || 'text',
      defaultValue: existingItem?.defaultValue || ''
    },
    buttons: [
      {
        type: 'cancel',
        text: 'Cancel'
      },
      {
        type: 'submit',
        text: existingPattern ? 'Update' : 'Insert',
        primary: true
      }
    ],
    onSubmit: async (dialogApi: any) => {
      const data = dialogApi.getData();
      const pattern = data.templatePattern.trim();

      if (!pattern) {
        editor.notificationManager.open({ text: 'Template pattern is required', type: 'error' });
        return;
      }

      // Save to API if collectionId is available
      if (collectionId) {
        try {
          if (existingItem) {
            await apiClient.updateTemplateItem(existingItem.id, {
              templatePattern: pattern,
              dataType: data.dataType,
              defaultValue: data.defaultValue || undefined
            });
          } else {
            await apiClient.createTemplateItem(collectionId, {
              templatePattern: pattern,
              dataType: data.dataType,
              defaultValue: data.defaultValue || undefined
            });
          }
        } catch (err: any) {
          const errorMsg = err?.response?.data?.error || err.message || 'Failed to save template item';
          editor.notificationManager.open({ text: errorMsg, type: 'error' });
          return;
        }
      }

      // Update or insert the template span in the editor
      const spanHtml = `<span class="mce-mustache-template" contenteditable="false" data-mce-pattern="${pattern}">{{${pattern}}}</span>`;

      if (targetElement && existingPattern) {
        // Update existing element
        targetElement.setAttribute('data-mce-pattern', pattern);
        targetElement.textContent = `{{${pattern}}}`;
      } else {
        // Insert new template at cursor
        editor.insertContent(spanHtml + '&nbsp;');
      }

      dialogApi.close();
    }
  });
}

// Dialog for filling in ALL template values at once (in BidBody context)
async function openFillAllDialog(editor: any, collectionId: number | null) {
  let templateItems: any[] = [];

  // Fetch all template item definitions
  if (collectionId) {
    try {
      const response = await apiClient.getTemplateItems(collectionId);
      if (response.success && response.items) {
        templateItems = response.items;
      }
    } catch (err) {
      console.error('Error fetching template items:', err);
    }
  }

  // Find all template spans in the editor to get current patterns
  const doc = editor.getDoc();
  const templateSpans: HTMLElement[] = Array.from(doc.querySelectorAll('.mce-mustache-template'));

  // Build a unique list of patterns from the editor content
  const patternsInEditor = new Set<string>();
  templateSpans.forEach((span: HTMLElement) => {
    const pattern = span.getAttribute('data-mce-pattern');
    if (pattern) {
      patternsInEditor.add(pattern);
    }
  });

  // If no patterns found in editor, nothing to fill
  if (patternsInEditor.size === 0) {
    editor.notificationManager.open({ text: 'No template fields found to fill', type: 'info' });
    return;
  }

  // Build dialog items for each pattern
  const dialogItems: any[] = [];
  const initialData: Record<string, string> = {};
  const patternList = Array.from(patternsInEditor);

  for (const pattern of patternList) {
    const templateItem = templateItems.find((item: any) => item.templatePattern === pattern);
    const dataType = templateItem?.dataType || 'text';
    const defaultValue = templateItem?.defaultValue || '';

    // Check if any span for this pattern already has a filled value
    const existingSpan = templateSpans.find(
      (span: HTMLElement) => span.getAttribute('data-mce-pattern') === pattern &&
        span.textContent !== `{{${pattern}}}`
    );
    const currentValue = existingSpan ? existingSpan.textContent || '' : '';

    const fieldName = `field_${pattern.replace(/[^a-zA-Z0-9_]/g, '_')}`;

    let placeholder = defaultValue || `Enter ${dataType} value`;
    if (dataType === 'price') placeholder = defaultValue || 'e.g., 99.99';
    if (dataType === 'date') placeholder = defaultValue || 'e.g., 2026-01-24';
    if (dataType === 'number') placeholder = defaultValue || 'e.g., 42';

    dialogItems.push({
      type: 'input',
      name: fieldName,
      label: `${pattern} (${dataType})`
    });

    initialData[fieldName] = currentValue || defaultValue || '';
  }

  editor.windowManager.open({
    title: 'Fill Template Values',
    size: 'medium',
    body: {
      type: 'panel',
      items: dialogItems
    },
    initialData,
    buttons: [
      {
        type: 'cancel',
        text: 'Cancel'
      },
      {
        type: 'submit',
        text: 'Apply Values',
        primary: true
      }
    ],
    onSubmit: (dialogApi: any) => {
      const data = dialogApi.getData();

      // Replace each template span with the provided value
      for (const pattern of patternList) {
        const fieldName = `field_${pattern.replace(/[^a-zA-Z0-9_]/g, '_')}`;
        const value = (data[fieldName] || '').trim();

        if (value) {
          // Find all spans with this pattern and replace with plain text
          const spans: HTMLElement[] = Array.from(
            doc.querySelectorAll(`.mce-mustache-template[data-mce-pattern="${pattern}"]`)
          );
          spans.forEach((span: HTMLElement) => {
            const textNode = doc.createTextNode(value);
            span.parentNode?.replaceChild(textNode, span);
          });
        }
      }

      dialogApi.close();
    }
  });
}
